import React, {useEffect, useState} from 'react';
import AuditLogDashboard from './AuditLogPage'; // Update this path based on your project structure
import {showToast} from '../../helpers/showToast';
import {
    exportAuditLogs,
    getAuditLogActions,
    getAuditLogActionTypes,
    getAuditLogRoles,
    getAuditLogs,
    getAuditLogUserTypes
} from '../../api/methods';

const AuditLogPageContainer = ({tab}) => {
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        performedBy: '',
        performedUsername: '',
        performedByRole: '', // This should match the exact parameter name in your API
        performedByUserType: '', // This should match the exact parameter name in your API
        taxNumber: '',
        activityNumber: '',
        note: '',
        affectedUserId: '',
        affectedUser: '',
        affectedDeviceId: '',
        affectedDeviceName: '',
        startDate: '',
        endDate: '',
        sortField: 'timestamp',
        sortDirection: 'desc'
    });
    const [pagination, setPagination] = useState({
        page: 0, size: 10, totalElements: 0, totalPages: 0
    });

    // Metadata for filters
    const [actionTypes, setActionTypes] = useState([]);
    const [roles, setRoles] = useState([]);
    const [userTypes, setUserTypes] = useState([]);
    const [actions, setActions] = useState([]);

    // Fetch audit logs with current filters and pagination
    const fetchAuditLogs = async (page = pagination.page, size = pagination.size, customFilters = null) => {
        setLoading(true);
        try {
            // Use custom filters if provided, otherwise use the state filters
            const filtersToUse = customFilters || filters;

            // Create a copy of filters for the API request
            const queryFilters = {
                ...filtersToUse, page, size
            };

            // Remove empty filter values to keep URL clean
            Object.keys(queryFilters).forEach(key => {
                if (queryFilters[key] === '' || queryFilters[key] === null || queryFilters[key] === undefined) {
                    delete queryFilters[key];
                }
            });

            console.log('Sending filters to API:', queryFilters);

            const response = await getAuditLogs(queryFilters);
            console.log('API Response:', response);

            if (response && response.content) {
                setAuditLogs(response.content);
                setPagination({
                    page: response.page,
                    size: response.size,
                    totalElements: response.totalElements,
                    totalPages: response.totalPages
                });
            } else {
                console.warn('Invalid response structure:', response);
            }
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            showToast({
                toastOptions: {type: "error",}, data: {
                    variant: 'error', text: "Failed to load audit logs. Please try again later",
                },
            });

        } finally {
            setLoading(false);
        }
    };

    // Fixed fetchFilterOptions function to properly handle enum arrays
    const fetchFilterOptions = async () => {
        try {
            // Fetch action types
            const actionTypesResponse = await getAuditLogActionTypes();
            console.log('Action types response:', actionTypesResponse);
            const actionTypesData = Array.isArray(actionTypesResponse) ? actionTypesResponse : (actionTypesResponse.data || []);

            const formattedActionTypes = actionTypesData.map(action => ({
                value: action, // The enum value as is
                label: formatActionType(action)
            }));
            setActionTypes(formattedActionTypes);

            //Fetch Actions
            const actionsResponse = await getAuditLogActions();
            console.log('Actions response:', actionsResponse);
            const actionsData = Array.isArray(actionsResponse) ? actionsResponse : (actionsResponse.data || []);

            const formattedActions = actionsData.map(action => ({
                value: action, // The enum value as is
                label: formatActionType(action)
            }));
            setActions(formattedActions);

            // Fetch roles
            const rolesResponse = await getAuditLogRoles();
            console.log('Roles response:', rolesResponse);
            const rolesData = Array.isArray(rolesResponse) ? rolesResponse : (rolesResponse.data || []);

            const formattedRoles = rolesData.map(role => ({
                value: role, // The enum value as is
                label: formatActionType(role)
            }));
            setRoles(formattedRoles);

            // Fetch user types
            const userTypesResponse = await getAuditLogUserTypes();
            console.log('User types response:', userTypesResponse);
            const userTypesData = Array.isArray(userTypesResponse) ? userTypesResponse : (userTypesResponse.data || []);

            const formattedUserTypes = userTypesData.map(type => ({
                value: type, // The enum value as is
                label: formatActionType(type)
            }));
            setUserTypes(formattedUserTypes);
        } catch (error) {
            console.error('Error fetching filter options:', error);
            // toast.error('Failed to load filter options. Some filters may not be available.');
        }
    };

    // Helper function to format enum strings for display
    const formatActionType = (value) => {
        if (!value) return '';
        return String(value)
            .replace(/_/g, ' ')
            .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    };

    // Initial data load
    useEffect(() => {
        fetchAuditLogs();
        fetchFilterOptions();
    }, []);

    // Handle page change
    const handlePageChange = (newPage) => {
        fetchAuditLogs(newPage, pagination.size);
    };

    // Handle page size change
    const handlePageSizeChange = (newSize) => {
        fetchAuditLogs(0, newSize);
    };

    // Handle filter change - now properly uses the new filters for API call
    const handleFilterChange = (newFilters) => {
        console.log('Filter changed to:', newFilters);
        setFilters(newFilters);
        // Pass the new filters directly to fetchAuditLogs
        fetchAuditLogs(0, pagination.size, newFilters);
    };

    // Handle sort change
    const handleSortChange = (field, direction) => {
        const newFilters = {
            ...filters, sortField: field, sortDirection: direction
        };
        setFilters(newFilters);
        // Use the new filters directly instead of waiting for state update
        fetchAuditLogs(pagination.page, pagination.size, newFilters);
    };

    // Export data as CSV
    const exportAsCsv = async () => {
        setLoading(true);
        try {
            if (!filters.action) {

                showToast({
                    toastOptions: {type: "error",}, data: {
                        text: "Please select an action at least",
                    },
                });
                return;
            }
            // Prepare export filters - remove empty values
            const exportFilters = {...filters, size: 1000};

            Object.keys(exportFilters).forEach(key => {
                if (exportFilters[key] === '' || exportFilters[key] === null || exportFilters[key] === undefined) {
                    delete exportFilters[key];
                }
            });



            const response = await exportAuditLogs(exportFilters);
            const href = URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = href;
            const date = new Date().toISOString().split('T')[0];
            link.setAttribute('download', `audit-logs-${date}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            showToast({
                toastOptions: {type: "success",}, data: {
                    variant: 'success', text: "Audit logs exported successfully",
                },
            });
        } catch (error) {
            console.error('Error exporting audit logs:', error);
            showToast({
                toastOptions: {type: "error",}, data: {
                    variant: 'error', text: "Failed to export audit logs. Please try again later.",
                },
            })
            // toast.error('Failed to export audit logs. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (<AuditLogDashboard
            tab={tab}
            auditLogs={auditLogs}
            loading={loading}
            pagination={pagination}
            filters={filters}
            actionTypes={actionTypes}
            roles={roles}
            userTypes={userTypes}
            actions={actions}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
            onExport={exportAsCsv}
            onRefresh={() => fetchAuditLogs(pagination.page, pagination.size)}
        />);
};

export default AuditLogPageContainer;
